import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  HostListener,
  AfterViewInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-drum-time-picker',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './time-picker.component.html',
  styleUrls: ['./time-picker.component.scss'],
})
export class DrumTimePickerComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() label: string = '';
  @Input() value: string = '';
  @Input() disabled: boolean = false;
  @Input() placeholder: string = 'HH:MM AM/PM';
  @Output() timeChange = new EventEmitter<string>();

  @ViewChild('hourDrum')   hourDrumEl!:   ElementRef<HTMLElement>;
  @ViewChild('minuteDrum') minuteDrumEl!: ElementRef<HTMLElement>;
  @ViewChild('periodDrum') periodDrumEl!: ElementRef<HTMLElement>;

  isOpen       = false;
  displayValue = '';
  hasValue     = false;  
  hours   = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
  minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
  periods = ['AM', 'PM'];

  selectedHour   = '12';
  selectedMinute = '00';
  selectedPeriod = 'AM';

  private readonly ITEM_HEIGHT = 44;
  private dragState: Map<string, { startY: number; startScroll: number; isDragging: boolean }> = new Map();

  ngOnInit() {
    this.parseValue(this.value);
  }

  ngAfterViewInit() {
    setTimeout(() => this.scrollToSelected(), 50);
  }

  ngOnDestroy() {}

  private parseValue(val: string) {
    if (!val) {
      this.hasValue    = false;
      this.displayValue = '';
      return;
    }
    const match = val.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (match) {
      this.selectedHour   = match[1].padStart(2, '0');
      this.selectedMinute = match[2];
      this.selectedPeriod = match[3].toUpperCase();
      this.displayValue   = `${this.selectedHour}:${this.selectedMinute} ${this.selectedPeriod}`;
      this.hasValue       = true;
    }
  }

  open() {
    if (this.disabled) return;
    this.isOpen = true;
    setTimeout(() => this.scrollToSelected(), 50);
  }

  close() {
    this.isOpen = false;
  }

  confirm() {
    this.displayValue = `${this.selectedHour}:${this.selectedMinute} ${this.selectedPeriod}`;
    this.hasValue     = true;
    this.timeChange.emit(this.displayValue);
    this.close();
  }

  cancel() {
    this.parseValue(this.value);  
    this.close();
  } 

  clear(e: MouseEvent) {
    e.stopPropagation();
    this.displayValue  = '';
    this.hasValue      = false;
    this.selectedHour  = '12';
    this.selectedMinute = '00';
    this.selectedPeriod = 'AM';
    this.value         = '';      
    this.timeChange.emit('');      
  }

  @HostListener('document:keydown.escape')
  onEscape() { if (this.isOpen) this.cancel(); }

  onBackdropClick(e: MouseEvent) {
    if ((e.target as HTMLElement).classList.contains('tp-backdrop')) this.cancel();
  }
 

  private snapDrum(el: HTMLElement, type: 'hour' | 'minute' | 'period') {
    const items    = type === 'hour' ? this.hours : type === 'minute' ? this.minutes : this.periods;
    const index    = Math.round(el.scrollTop / this.ITEM_HEIGHT);
    const clamped  = Math.max(0, Math.min(index, items.length - 1));
    el.scrollTo({ top: clamped * this.ITEM_HEIGHT, behavior: 'smooth' });
    if (type === 'hour')   this.selectedHour   = this.hours[clamped];
    if (type === 'minute') this.selectedMinute = this.minutes[clamped];
    if (type === 'period') this.selectedPeriod = this.periods[clamped];
  }

  onDrumScroll(e: Event, type: 'hour' | 'minute' | 'period') {
    const el = e.target as HTMLElement;
    clearTimeout((el as any).__snapTimer);
    (el as any).__snapTimer = setTimeout(() => this.snapDrum(el, type), 120);
  }

  selectItem(type: 'hour' | 'minute' | 'period', index: number) {
    const el = type === 'hour'   ? this.hourDrumEl?.nativeElement
             : type === 'minute' ? this.minuteDrumEl?.nativeElement
             :                     this.periodDrumEl?.nativeElement;
    if (!el) return;
    el.scrollTo({ top: index * this.ITEM_HEIGHT, behavior: 'smooth' });
    if (type === 'hour')   this.selectedHour   = this.hours[index];
    if (type === 'minute') this.selectedMinute = this.minutes[index];
    if (type === 'period') this.selectedPeriod = this.periods[index];
  }

  isSelected(type: 'hour' | 'minute' | 'period', val: string): boolean {
    if (type === 'hour')   return this.selectedHour   === val;
    if (type === 'minute') return this.selectedMinute === val;
    return this.selectedPeriod === val;
  }

  private scrollToSelected() {
    const scrollDrum = (el: HTMLElement | undefined, items: string[], selected: string) => {
      if (!el) return;
      const idx = items.indexOf(selected);
      if (idx >= 0) el.scrollTop = idx * this.ITEM_HEIGHT;
    };
    scrollDrum(this.hourDrumEl?.nativeElement,   this.hours,   this.selectedHour);
    scrollDrum(this.minuteDrumEl?.nativeElement, this.minutes, this.selectedMinute);
    scrollDrum(this.periodDrumEl?.nativeElement, this.periods, this.selectedPeriod);
  }

  onTouchStart(e: TouchEvent, type: 'hour' | 'minute' | 'period') {
    const el = e.currentTarget as HTMLElement;
    this.dragState.set(type, { startY: e.touches[0].clientY, startScroll: el.scrollTop, isDragging: true });
  }

  onTouchMove(e: TouchEvent, type: 'hour' | 'minute' | 'period') {
    const state = this.dragState.get(type);
    if (!state?.isDragging) return;
    const el = e.currentTarget as HTMLElement;
    el.scrollTop = state.startScroll + (state.startY - e.touches[0].clientY);
    e.preventDefault();
  }

  onTouchEnd(e: TouchEvent, type: 'hour' | 'minute' | 'period') {
    this.snapDrum(e.currentTarget as HTMLElement, type);
    this.dragState.delete(type);
  }
}